import apiClient from './apiClient';
import { decryptPatientList, decryptPatient } from '../utils/encryption';

/**
 * Patient API Service
 * Handles fetching patient data from the backend and decrypting it
 */

/**
 * Fetch list of patients with pagination, search, and sorting
 * @param {Object} options - Query options
 * @param {number} options.limit - Number of records per page
 * @param {number} options.skip - Number of records to skip
 * @param {string} options.search - Search term
 * @param {boolean} options.all - Fetch all patients without limit (for dropdown)
 * @param {string} options.sortBy - Field to sort by (name, age, emergencyContact, createdAt, patientNumber)
 * @param {string} options.sortOrder - Sort order (asc, desc)
 * @returns {Promise<Object>} { data, total, limit, skip }
 */
export const fetchPatients = async (options = {}) => {
  try {
    const { limit = 25, skip = 0, search = '', all = false, sortBy, sortOrder } = options;

    const params = {};

    // If 'all' is true, don't set limit/skip (fetch all patients)
    if (all) {
      params.all = true;
    } else {
      params.$limit = limit;
      params.$skip = skip;
    }

    if (search) {
      params.search = search;
    }

    // Add sorting parameters
    if (sortBy) {
      params.sortBy = sortBy;
      params.sortOrder = sortOrder || 'asc';
    }

    const response = await apiClient.get('/api/medical-patients', { params });

    // Decrypt patient data before returning
    const decryptedData = decryptPatientList(response.data.data);

    return {
      data: decryptedData,
      total: response.data.total || 0,
      limit: response.data.limit || limit,
      skip: response.data.skip || skip,
    };
  } catch (error) {
    console.error('Failed to fetch patients:', error);

    // Handle specific error cases
    if (error.response?.status === 401) {
      throw new Error('Authentication required. Please login again.');
    } else if (error.response?.status === 403) {
      throw new Error('Access denied. Only medical administrators can view patient lists.');
    } else if (error.response?.status === 404) {
      throw new Error('Patient service not found.');
    } else {
      throw new Error(error.response?.data?.message || 'Failed to fetch patients. Please try again.');
    }
  }
};

/**
 * Fetch a single patient by ID
 * @param {string} patientId - Patient ID
 * @returns {Promise<Object>} Decrypted patient object
 */
export const fetchPatientById = async (patientId) => {
  try {
    const response = await apiClient.get(`/api/medical-patients/${patientId}`);

    // Decrypt patient data before returning
    const decryptedPatient = decryptPatient(response.data);

    return decryptedPatient;
  } catch (error) {
    console.error(`Failed to fetch patient ${patientId}:`, error);

    if (error.response?.status === 401) {
      throw new Error('Authentication required. Please login again.');
    } else if (error.response?.status === 403) {
      throw new Error('Access denied. Only medical administrators can view patient details.');
    } else if (error.response?.status === 404) {
      throw new Error('Patient not found.');
    } else {
      throw new Error(error.response?.data?.message || 'Failed to fetch patient. Please try again.');
    }
  }
};

/**
 * Hard-delete a patient and all related records (cascade).
 * Admin-only. Backend removes events, refills, notes, consumes,
 * notifications, invites, linked user, and pulls from HCP lists.
 * @param {string} patientId
 * @returns {Promise<Object>} delete summary
 */
export const deletePatient = async (patientId) => {
  try {
    const response = await apiClient.delete(`/api/medical-patients/${patientId}`);
    return response.data;
  } catch (error) {
    console.error(`Failed to delete patient ${patientId}:`, error);

    if (error.response?.status === 401) {
      throw new Error('Authentication required. Please login again.');
    } else if (error.response?.status === 403) {
      throw new Error('Access denied. Only medical administrators can delete patients.');
    } else if (error.response?.status === 404) {
      throw new Error('Patient not found.');
    } else {
      throw new Error(error.response?.data?.message || 'Failed to delete patient. Please try again.');
    }
  }
};

/**
 * Get patient statistics (if needed)
 * @returns {Promise<Object>} Patient statistics
 */
export const fetchPatientStats = async () => {
  try {
    // Fetch first page to get total count
    const response = await apiClient.get('/api/medical-patients', {
      params: { $limit: 1 }
    });

    return {
      totalPatients: response.data.total || 0,
    };
  } catch (error) {
    console.error('Failed to fetch patient stats:', error);
    return {
      totalPatients: 0,
    };
  }
};
